import { GridUtils } from '@deephaven/grid';
import clamp from 'lodash.clamp';
import ColumnHeaderGroup from "../../ColumnHeaderGroup.js";
/**
 * Moves an item as the result of drag and drop in the visibility ordering builder
 * This is extracted from the component mostly for testing purposes
 * Drag and drop interactions are not easily testable
 *
 * @param from The item being dragged
 * @param to The item being dropped on
 * @param movedColumns The existing moved columns
 * @param columnHeaderGroups The column header groups pre-drag
 * @param flattenedItems Flattened tree items representing the list
 * @param selectedParentItems The user selected items in tree order. Should not include items if their parent item is selected
 * @param firstMovableIndex The first visible index that can be moved
 * @param lastMovableIndex The last visible index that can be moved
 * @returns Object containing the updated column header groups and updated movedColumns array
 *          The movedColumns returned is complete and does not need to be combined with the existing movedColumns
 */
export function moveItemsFromDrop(from, to, movedColumns, columnHeaderGroups, selectedParentItems, firstMovableIndex, lastMovableIndex) {
  var newMoves = [];
  var newGroups = columnHeaderGroups;
  var firstVisibleIndex = selectedParentItems[0].data.visibleIndex;
  var fromItemIndex = from.index;
  var toItemIndex = to.index;
  var toIndex = Array.isArray(firstVisibleIndex) ? firstVisibleIndex[1] + 1 : firstVisibleIndex + 1;
  newGroups = moveToGroup(selectedParentItems[0], to.parentId, newGroups);

  // Move the items after to all after the first selected item
  for (var i = 1; i < selectedParentItems.length; i += 1) {
    var visibleIndex = selectedParentItems[i].data.visibleIndex;
    newMoves = GridUtils.moveItemOrRange(visibleIndex, toIndex, newMoves, true);
    toIndex += Array.isArray(visibleIndex) ? visibleIndex[1] - visibleIndex[0] + 1 : 1;
    newGroups = moveToGroup(selectedParentItems[i], to.parentId, newGroups);
  }
  var selectedRange = [Array.isArray(firstVisibleIndex) ? firstVisibleIndex[0] : firstVisibleIndex, toIndex - 1];
  var originalDropIndex = Array.isArray(to.data.visibleIndex) ? to.data.visibleIndex[0] : to.data.visibleIndex;
  var dropIndex = GridUtils.getVisibleIndex(originalDropIndex, newMoves);

  // When moving up from multi-select
  // And the items caused the drop index to shift (disjoint multi-select)
  // The drop index will be off by 1
  if (fromItemIndex > toItemIndex && dropIndex > originalDropIndex) {
    dropIndex -= 1;
  }
  // Dropping as first item in a group
  // Need to adjust visible index if dragging from before this group or it is off by 1
  if (to.children.length > 0 && (Array.isArray(firstVisibleIndex) ? firstVisibleIndex[0] : firstVisibleIndex) < dropIndex) {
    dropIndex -= 1;
  }
  if (selectedRange[0] < dropIndex) {
    dropIndex -= selectedRange[1] - selectedRange[0];
  }
  newMoves = GridUtils.moveItemOrRange(selectedRange, clamp(dropIndex, firstMovableIndex, lastMovableIndex), newMoves);
  return {
    groups: newGroups,
    movedColumns: newMoves.length > 0 ? movedColumns.concat(newMoves) : movedColumns
  };
}
export function moveToGroup(item, toName, columnGroups) {
  if (item.parentId === toName) {
    // Don't need to move an item if it is already in the group
    return columnGroups;
  }
  var newGroups = columnGroups.map(group => new ColumnHeaderGroup(group));
  var newGroupMap = new Map(newGroups.map(group => [group.name, group]));
  var fromGroup = newGroups.find(g => g.name === item.parentId);
  var toGroup = newGroups.find(g => g.name === toName);
  var movedGroup = newGroups.find(g => g.name === item.id);
  if (fromGroup != null) {
    // Moved out of a group
    fromGroup.children = fromGroup.children.filter(name => name !== item.id);

    // Moved all children out of a group
    if (fromGroup.children.length === 0) {
      var _parent;
      var deleteNames = new Set([fromGroup.name]);
      var _fromGroup$parent = fromGroup.parent,
        parentName = _fromGroup$parent === void 0 ? '' : _fromGroup$parent;
      var parent = newGroupMap.get(parentName);
      (_parent = parent) === null || _parent === void 0 || _parent.removeChildren([fromGroup.name]);

      // Might need to delete parents if their only child is the now empty group
      while (parent && parent.children.length === 0) {
        var _parent$parent, _parent2;
        deleteNames.add(parent.name);
        var nextParentName = (_parent$parent = (_parent2 = parent) === null || _parent2 === void 0 ? void 0 : _parent2.parent) !== null && _parent$parent !== void 0 ? _parent$parent : '';
        var nextParent = newGroupMap.get(nextParentName);
        nextParent === null || nextParent === void 0 || nextParent.removeChildren([parent.name]);
        parent = nextParent;
      }

      // Delete all groups that are now empty
      newGroups = newGroups.filter(_ref => {
        var name = _ref.name;
        return !deleteNames.has(name);
      });
    }
  }
  if (toGroup != null) {
    // Moved into a group
    toGroup.addChildren([item.id]);
  }
  movedGroup === null || movedGroup === void 0 || movedGroup.setParent(toName !== null && toName !== void 0 ? toName : undefined);
  return newGroups;
}
//# sourceMappingURL=VisibilityOrderingBuilderUtils.js.map